import { Loader2, Package } from 'lucide-react';
import _ from 'lodash';
import { Input } from '../../ui/input';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext } from '../../ui/pagination';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Select from 'src/components/ui/BrandSelect';
import { Actions as ADMIN_ACTIONS } from '../../../store/actions/adminActions';
import { Button } from '../../ui/button';
import Breadcrumb from '../../Common/Breadcrumb';
import BulkEditModal from '../../Modals/BulkEditModal';
import { convertToTitleCase, getUsageTypeDetails, itemToOptions } from '../../utils/GlobalFunctions';
import { adminPortalSelect } from '../../utils/ReactSelectStyles';
import ConversionFactorsTable from './ConversionFactorsTable';

const filtersStyleInputs = {
  display: 'flex',
  width: '-webkit-fill-available',
  flexDirection: 'row',
  marginLeft: 10,
  marginRight: 10,
  marginBottom: 10,
  justifyContent: 'space-between',
  alignItems: 'center',
};

const ConversionFactors = ({
  bulkEditModal,
  toggleModal,
  branding,
  options,
  canEditAdminPortal,
  filterSize,
  itemCount,
  getItemsByTermsFromIndex,
  adminPortalModal,
  confirmationModal,
  getAllItemsFromDB,
  addOns,
  usageTypeDetails,
  subentities,
  repackageFactors,
  showLoader
}) => {
  const filterPanelFontFamily =
    branding?.fonts?.[0]?.font?.body || "ui-sans-serif, system-ui, sans-serif, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
  const tableName = 'conversionFactors';

  const [showHide, setShowHide] = useState(false);
  const [page, setPage] = useState(1);
  const [tableSize, setTableSize] = useState(filterSize?.[0]);
  const [totalItems, setTotalItems] = useState(null);
  const [filter, setFilter] = useState({});
  const [showPagination, setShowPagination] = useState(false);

  const handleFilter = (selectedFilter, e) => {
    const newFilter = { ...filter };
    const filterName = e?.target?.name ?? e?.name;

    if (e?.action === 'clear') {
      if (filterName) delete newFilter[filterName];
      if (filterName === 'entity') delete newFilter.subentity;
    } else if (e?.target?.type === 'text') {
      newFilter[filterName] = e?.target?.value;
    } else if (filterName === 'entity') {
      newFilter.entity = selectedFilter?.value;
      const selectedVal = selectedFilter?.value;
      const subentitiesForEntity = subentities?.filter((subentity) => {
        const ent = subentity?.entity;
        if (ent == null || selectedVal == null) return false;
        if (Array.isArray(ent)) return ent.map(String).includes(String(selectedVal));
        return String(ent).includes(String(selectedVal));
      });
      if (subentitiesForEntity?.length > 0) {
        newFilter.subentity = _.uniq(subentitiesForEntity.map((subentity) => subentity.id));
      } else {
        delete newFilter.subentity;
      }
    } else if (filterName) {
      newFilter[filterName] = selectedFilter?.value;
    }
    setFilter(newFilter);
  };

  const handlePageChange = (_, value) => {
    setPage(value);
    getItemsByTermsFromIndex(tableName, filter, value, tableSize?.value);
  };

  const handleTableSizeChange = (selected) => {
    setTableSize(selected);
    setPage(1);
    getItemsByTermsFromIndex(tableName, filter, 1, selected?.value);
  };

  useEffect(() => {
    (async () => {
      if (!addOns) {
        await getAllItemsFromDB('addOns');
      }
      if (!usageTypeDetails) {
        await getAllItemsFromDB('usageTypeDetails');
      }
    })();
    if (showHide) {
      setShowPagination(true);
    }
  }, [showHide, page, tableSize, adminPortalModal, confirmationModal, addOns, usageTypeDetails, getAllItemsFromDB]);

  const items = [{ label: 'Conversion Factors', link: '/admin/conversionfactors' }];
  const total = totalItems ? totalItems : itemCount;
  return (
    <>
      <div className='flex flex-row justify-between items-center'>
        <Breadcrumb items={items} />
        <div className='flex flex-row w-auto items-center'>
          <div
            className={`mr-[1.5vh] flex flex-row flex-wrap items-center gap-3 ${canEditAdminPortal ? '' : 'hidden'}`}>
            <Button disabled={showLoader} onClick={() => repackageFactors('conversionFactors')} className='inline-flex items-center'>
            {showLoader ? <Loader2 size={16} strokeWidth={1.5} className="animate-spin mr-1" /> : <Package size={16} strokeWidth={1.5} className="mr-1" />}
              Re-Package
            </Button>

            <Button
              onClick={() => {
                const defaultState =
                  Array.isArray(usageTypeDetails) &&
                  usageTypeDetails.reduce((acc, usageType) => {
                    acc[usageType.type] = null;
                    return acc;
                  }, {});
                toggleModal(true, 'conversionFactors-table-add', defaultState, null, {
                  filters: filter,
                  page,
                  tableSize: tableSize.value,
                });
              }}
              className='inline-flex items-center'>
              Add a Conversion Factor
            </Button>
          </div>
        </div>
      </div>

      {!showHide && (
        <section className='panel font-sans' style={{ fontFamily: filterPanelFontFamily }}>
          <header
            className='panel-heading font-sans'
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', fontFamily: filterPanelFontFamily }}>
            Conversion Factor Filters
          </header>
          <div className='panel-body' style={{ height: 'auto' }}>
            {Object.keys(options)?.map((optionType, index) => {
              const notUsageTypes = ['metric', 'addOns', 'year', 'subentity', 'entity'].includes(optionType);
              let label = convertToTitleCase(optionType);
              if (!notUsageTypes) {
                label = getUsageTypeDetails(usageTypeDetails, optionType)?.displayName;
              }
              return options[optionType] !== 'SearchByText' ? (
                <div key={index} style={filtersStyleInputs}>
                  <label className='font-sans' style={{ fontSize: 16, marginBottom: 0, fontFamily: filterPanelFontFamily }}>{label}</label>
                  <div style={{ width: '80%' }}>
                    <Select
                      id={'select-' + optionType}
                      options={options?.[optionType]}
                      name={optionType}
                      style={adminPortalSelect}
                      onChange={(selected, e) => handleFilter(selected, e)}
                      isClearable={true}
                    />
                  </div>
                </div>
              ) : (
                <div key={index} style={filtersStyleInputs}>
                  <label className='font-sans' style={{ fontSize: 16, marginBottom: 0, fontFamily: filterPanelFontFamily }}>{convertToTitleCase(optionType)}</label>
                  <div style={{ width: '80%' }}>
                    <Input
                      id='outlined-basic'
                      onChange={(e) => handleFilter(null, e)}
                      placeholder='Search By Id'
                      name='id'
                      className='w-full'
                    />
                  </div>
                </div>
              );
            })}
            <div style={{ display: 'grid', placeItems: 'center', marginTop: 10 }}>
              <Button
                id='search-btn'
                onClick={() => {
                  setPage(1);
                  getItemsByTermsFromIndex(tableName, filter, 1, tableSize?.value);
                  setShowHide(true);
                }}
                disabled={_.isEmpty(filter)}
                style={{ width: '20vh', fontSize: 16 }}>
                Search
              </Button>
            </div>
          </div>
        </section>
      )}

      {showHide && (
        <>
          <ConversionFactorsTable
            page={page}
            setTableSize={setTableSize}
            handleTableSizeChange={handleTableSizeChange}
            tableSize={tableSize}
            setTotalItems={setTotalItems}
            setShowHide={setShowHide}
            setFilter={setFilter}
            filter={filter}
            setPage={setPage}
            options={options}
            setShowPagination={setShowPagination}
            canEditAdminPortal={canEditAdminPortal}
          />

          {total > 0 && showPagination && (() => {
            const pageCount = Math.ceil(total / tableSize?.value);
            return (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious onClick={() => page > 1 && handlePageChange(null, page - 1)} />
                  </PaginationItem>
                  {Array.from({ length: pageCount }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === pageCount || Math.abs(p - page) <= 2)
                    .map((p, idx, arr) => (
                      <React.Fragment key={p}>
                        {idx > 0 && arr[idx - 1] < p - 1 && <PaginationItem><span className='px-2'>...</span></PaginationItem>}
                        <PaginationItem>
                          <PaginationLink isActive={p === page} onClick={() => handlePageChange(null, p)}>{p}</PaginationLink>
                        </PaginationItem>
                      </React.Fragment>
                    ))}
                  <PaginationItem>
                    <PaginationNext onClick={() => page < pageCount && handlePageChange(null, page + 1)} />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            );
          })()}
        </>
      )}

      <Dialog open={!!bulkEditModal} onOpenChange={(open) => !open && toggleModal()}>
        <DialogContent>
          <BulkEditModal />
        </DialogContent>
      </Dialog>
    </>
  );
};

const mapStateToProps = (state) => {
  const options = {};
  const optionTypes = state?.metric?.types;
  const entities = state?.entities;
  const subentities = state?.subentities;
  const metrics = state?.metric?.list;
  const addOns = state?.admin?.adminPortal?.addOns;
  const usageTypeDetails = state?.admin?.adminPortal?.usageTypeDetails;
  //const entities = state?.entities;

  options.year = [...new Array(new Date().getFullYear() - 2012)]
    .map((_, index) => {
      return {
        label: (2013 + index).toString(),
        value: (2013 + index).toString(),
      };
    })
    .sort((a, b) => b.label.localeCompare(a.label));
  options.entity = entities
    ?.concat({ name: '<null>', id: 'NA' })
    ?.map(itemToOptions)
    ?.sort((a, b) => a.label.localeCompare(b.label));
  options.subentity = subentities
    ?.concat({ name: '<null>', id: 'NA' })
    ?.map(itemToOptions)
    ?.sort((a, b) => a.label.localeCompare(b.label));

  optionTypes &&
    Object.keys(optionTypes)?.map((item) => {
      options[item] = optionTypes[item]
        ?.concat({ name: '<null>', id: 'NA' })
        ?.map(itemToOptions)
        ?.sort((a, b) => a.label.localeCompare(b.label));
      return options;
    });

  options.metric = metrics
    ?.concat({ name: '<null>', id: 'NA' })
    ?.map(itemToOptions)
    ?.sort((a, b) => a.label.localeCompare(b.label));
  options.addOns = addOns?.map(itemToOptions)?.sort((a, b) => a.label.localeCompare(b.label));

  return {
    options,
    bulkEditModal: state?.admin?.modal?.bulkEditModal,
    branding: state.branding,
    filterSize: state?.admin?.filterSize,
    itemCount: state?.admin?.termsItems?.totalCount,
    adminPortalModal: state.admin?.modal?.adminPortalModal,
    confirmationModal: state.admin?.modal?.confirmationModal,
    openSearchTerms: state?.admin?.openSearchTerms,
    addOns,
    usageTypeDetails,
    subentities,
    showLoader: state?.showLoader
  };
};

const mapDispatchToProps = (dispatch) => ({
  getAllItemsFromDB: (table) => dispatch(ADMIN_ACTIONS.getAllItemsFromDB(table)),
  toggleModal: (status, action, item, modalProps, notAutoClose, openSearchTerms) =>
    dispatch(ADMIN_ACTIONS.toggleModal(status, action, item, modalProps, notAutoClose, openSearchTerms)),
  getItemsByTermsFromIndex: (index, filters, page, size) =>
    dispatch(ADMIN_ACTIONS.getItemsByTermsFromIndex(index, filters, page, size)),
  repackageFactors: (type) => dispatch(ADMIN_ACTIONS.repackageFactors(type)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConversionFactors);
